import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  BadgeStatus,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  ErrorState,
  PageHeader,
  SkeletonTeks,
  useToast,
  type KolomTabel,
} from '@/components'
import { api, downloadBerkas, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal, formatTanggalJam } from '@/lib/format'
import type { AlokasiPembayaran, Tagihan } from '@/lib/types'
import { useApi, useAksi } from '@/lib/useApi'
import { useAuth } from '@/lib/auth'

export default function DetailTagihan() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { punyaIzin } = useAuth()
  const { data, memuat, galat, kodeGalat, muat } = useApi<Tagihan>(id ? `/invoices/${id}` : null)
  const aksi = useAksi()
  const [konfirmasi, setKonfirmasi] = useState<'post' | 'cancel' | null>(null)
  const [unduh, setUnduh] = useState(false)

  if (galat) {
    return (
      <div>
        <PageHeader
          judul="Detail Tagihan"
          remah={
            <Link to="/tagihan" className="text-yayasan-800 hover:underline">
              ← Kembali ke daftar tagihan
            </Link>
          }
        />
        <ErrorState pesan={galat} kode={kodeGalat} onCoba={muat} />
      </div>
    )
  }

  const kolomBaris: KolomTabel<NonNullable<Tagihan['lines']>[number]>[] = [
    { kunci: 'lineNumber', judul: '#', lebar: '56px' },
    { kunci: 'description', judul: 'Uraian' },
    { kunci: 'periodLabel', judul: 'Periode', render: (b) => b.periodLabel ?? '—' },
    { kunci: 'quantity', judul: 'Qty', rata: 'kanan', render: (b) => String(b.quantity) },
    { kunci: 'unitPrice', judul: 'Harga Satuan', rata: 'kanan', render: (b) => formatRupiah(b.unitPrice) },
    { kunci: 'discountAmount', judul: 'Diskon', rata: 'kanan', render: (b) => formatRupiah(b.discountAmount) },
    { kunci: 'totalAmount', judul: 'Jumlah', rata: 'kanan', render: (b) => formatRupiah(b.totalAmount) },
  ]

  const kolomAlokasi: KolomTabel<AlokasiPembayaran>[] = [
    {
      kunci: 'allocatedAt',
      judul: 'Tanggal Alokasi',
      render: (b) => formatTanggalJam(b.allocatedAt),
    },
    { kunci: 'allocatedAmount', judul: 'Jumlah', rata: 'kanan', render: (b) => formatRupiah(b.allocatedAmount) },
    {
      kunci: 'reversedAt',
      judul: 'Status',
      render: (b) =>
        b.reversedAt ? (
          <Badge nada="bahaya">Dibatalkan {b.reversalReason ? `— ${b.reversalReason}` : ''}</Badge>
        ) : (
          <Badge nada="sukses">Aktif</Badge>
        ),
    },
  ]

  const unduhKuitansi = async () => {
    if (!id || !data) return
    setUnduh(true)
    try {
      await downloadBerkas(`/invoices/${id}/receipt`, `kuitansi-${data.invoiceNumber}.pdf`, { format: 'pdf' })
      toast.sukses('Kuitansi PDF berhasil diunduh.')
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setUnduh(false)
    }
  }

  return (
    <div>
      <PageHeader
        judul={data ? `Tagihan ${data.invoiceNumber}` : 'Detail Tagihan'}
        remah={
          <Link to="/tagihan" className="text-yayasan-800 hover:underline">
            ← Kembali ke daftar tagihan
          </Link>
        }
        deskripsi={
          data
            ? `${data.student?.person?.fullName ?? ''} · ${data.student?.studentNumber ?? ''} · ${data.unit?.name ?? ''}`
            : undefined
        }
        aksi={
          data ? (
            <div className="flex flex-wrap gap-2">
              {punyaIzin('invoice:approve', 'invoice:publish') && data.status === 'DRAFT' ? (
                <Button onClick={() => setKonfirmasi('post')}>Posting Tagihan</Button>
              ) : null}
              {punyaIzin('invoice:cancel') && data.status !== 'CANCELLED' ? (
                <Button varian="bahaya" onClick={() => setKonfirmasi('cancel')}>
                  Batalkan
                </Button>
              ) : null}
              <Button varian="garis" memuat={unduh} onClick={() => void unduhKuitansi()}>
                Buat Kuitansi PDF
              </Button>
              <Button varian="halus" onClick={() => navigate('/pembayaran')}>
                Catat Pembayaran
              </Button>
            </div>
          ) : null
        }
      />

      {memuat && !data ? (
        <Card>
          <SkeletonTeks baris={6} />
        </Card>
      ) : null}

      {data ? (
        <div className="space-y-5">
          <Card>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Info label="Status">
                <BadgeStatus status={data.status} />
              </Info>
              <Info label="Tanggal Tagihan">{formatTanggal(data.invoiceDate)}</Info>
              <Info label="Jatuh Tempo">{formatTanggal(data.dueDate)}</Info>
              <Info label="Periode">{data.periodLabel ?? '—'}</Info>
              <Info label="Subtotal">{formatRupiah(data.subtotal)}</Info>
              <Info label="Diskon">{formatRupiah(data.discountAmount)}</Info>
              <Info label="Total">{formatRupiah(data.totalAmount)}</Info>
              <Info label="Dibayar">{formatRupiah(data.paidAmount)}</Info>
              <Info label="Sisa Tagihan">
                <span className="font-semibold text-red-700">{formatRupiah(data.outstandingAmount)}</span>
              </Info>
              <Info label="Catatan">{data.notes ?? '—'}</Info>
            </div>
          </Card>

          <Card padat className="overflow-hidden">
            <div className="border-b border-netral-200 px-4 py-2.5">
              <h2 className="text-sm font-semibold text-netral-800">Baris Tagihan</h2>
            </div>
            <DataTable
              kolom={kolomBaris}
              data={data.lines ?? []}
              kunciBaris={(b) => b.id}
              pesanKosong="Tagihan ini tidak memiliki baris."
            />
          </Card>

          <Card>
            <CardHeader
              judul="Riwayat Pembayaran & Alokasi"
              deskripsi="Setiap alokasi mengurangi sisa tagihan. Alokasi yang dibatalkan tidak dihitung."
            />
            <div className="mt-3">
              <DataTable
                kolom={kolomAlokasi}
                data={data.allocations ?? []}
                kunciBaris={(b) => b.id}
                pesanKosong="Belum ada pembayaran yang dialokasikan ke tagihan ini."
              />
            </div>
          </Card>
        </div>
      ) : null}

      <ConfirmDialog
        terbuka={konfirmasi === 'post'}
        onTutup={() => setKonfirmasi(null)}
        judul="Posting Tagihan"
        pesan="Tagihan yang diposting akan membentuk jurnal piutang dan tidak dapat diubah."
        labelKonfirmasi="Posting"
        memuat={aksi.memuat}
        onKonfirmasi={async () => {
          const hasil = await aksi.jalankan(() => api.post(`/invoices/${id}/post`, {}), {
            sukses: 'Tagihan berhasil diposting.',
          })
          if (hasil !== null) {
            setKonfirmasi(null)
            muat()
          }
        }}
      />

      <ConfirmDialog
        terbuka={konfirmasi === 'cancel'}
        onTutup={() => setKonfirmasi(null)}
        judul="Batalkan Tagihan"
        pesan="Pembatalan tagihan bersifat permanen dan akan tercatat pada audit log. Tuliskan alasan pembatalan."
        labelKonfirmasi="Batalkan Tagihan"
        labelAlasan="Alasan pembatalan"
        minimalAlasan={5}
        memuat={aksi.memuat}
        onKonfirmasi={async (alasan) => {
          if (alasan.trim().length < 5) return
          const hasil = await aksi.jalankan(() => api.post(`/invoices/${id}/cancel`, { reason: alasan }), {
            sukses: 'Tagihan dibatalkan.',
          })
          if (hasil !== null) {
            setKonfirmasi(null)
            muat()
          }
        }}
      />
    </div>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm break-words text-netral-800">{children}</p>
    </div>
  )
}
